import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  type MouseEvent,
  useMemo,
  useRef,
  useState,
} from 'react';
import { MouseTooltip } from '@components/Tooltip';
import styles from './AccountTable.module.scss';

export interface Column<T extends Record<string, any>> {
  id: string;
  label: string;
  data?: React.ReactNode[];
}

export interface Props<T extends Record<string, any>> {
  columns: Column<T>[];
  data?: T[];
  className?: string;
  getRowId?: (row: T, index: number) => React.Key;
  onRowClick?: (key: React.Key, row: T) => void;
  selectedKey?: React.Key | React.Key[];
}

function EllipsisText({
  value,
  className,
}: {
  value: React.ReactNode;
  className: string;
}): React.JSX.Element {
  const ref = useRef<HTMLSpanElement | null>(null);
  const [tooltipText, setTooltipText] = useState<string | undefined>(undefined);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [showTooltip, setShowTooltip] = useState(false);

  const plainText =
    typeof value === 'string' || typeof value === 'number'
      ? String(value)
      : undefined;

  const updateTooltip = useCallback(() => {
    const el = ref.current;
    if (!el || !plainText) {
      setTooltipText(undefined);
      return;
    }

    const isTruncated = el.scrollWidth > el.clientWidth;
    setTooltipText(isTruncated ? plainText : undefined);
  }, [plainText]);

  useLayoutEffect(() => {
    updateTooltip();
  }, [updateTooltip]);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => updateTooltip());
    observer.observe(el);

    return () => observer.disconnect();
  }, [updateTooltip]);

  const handleMouseEnter = useCallback(
    (event: MouseEvent<HTMLSpanElement>) => {
      if (!tooltipText) return;
      setTooltipPos({ x: event.clientX, y: event.clientY });
      setShowTooltip(true);
    },
    [tooltipText],
  );

  const handleMouseMove = useCallback((event: MouseEvent<HTMLSpanElement>) => {
    if (!showTooltip) return;
    setTooltipPos({ x: event.clientX, y: event.clientY });
  }, [showTooltip]);

  const handleMouseLeave = useCallback(() => {
    setShowTooltip(false);
  }, []);

  return (
    <>
      <span
        ref={ref}
        className={className}
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {value}
      </span>
      {showTooltip && tooltipText && (
        <MouseTooltip x={tooltipPos.x} y={tooltipPos.y}>
          <span>{tooltipText}</span>
        </MouseTooltip>
      )}
    </>
  );
}

function AccountTable<T extends Record<string, any>>({
  columns,
  data = [],
  className = '',
  getRowId,
  onRowClick,
  selectedKey,
}: Props<T>): React.JSX.Element {
  const [hoveredKey, setHoveredKey] = useState<React.Key | undefined>(
    undefined,
  );

  const resolvedData = useMemo<T[]>(() => {
    if (data.length > 0) return data;

    const hasColumnData = columns.some((col) => Array.isArray(col.data));
    if (!hasColumnData) return [];

    const rowCount = columns.reduce(
      (max, col) => Math.max(max, col.data?.length ?? 0),
      0,
    );

    return Array.from({ length: rowCount }, (_, rowIndex) => {
      const row = {} as Record<string, React.ReactNode>;
      columns.forEach((col) => {
        row[col.id] = col.data?.[rowIndex];
      });
      return row as T;
    });
  }, [columns, data]);

  const getKey = getRowId ?? ((_, i) => i);

  const isRowSelected = (
    key: React.Key,
    selected?: React.Key | React.Key[],
  ) => {
    if (!selected) return false;
    if (Array.isArray(selected)) {
      return selected.map(String).includes(String(key));
    }
    return String(selected) === String(key);
  };

  const renderCellValue = (value: React.ReactNode) => {
    const normalizedValue =
      value === null || value === undefined || value === '' ? '-' : value;

    return (
      <EllipsisText className={styles.bodyLabel} value={normalizedValue} />
    );
  };

  return (
    <>
      {columns.map((col) => (
        <div
          key={col.id}
          id={col.id}
           className={`${styles.column} ${styles[col.id as keyof typeof styles] ?? ''}`}
        >
          <div className={styles.header}>
            <span className={styles.headerLabel}>{col.label}</span>
          </div>
          {resolvedData.map((row, rowIndex) => {
            const key = getKey(row, rowIndex);
            const isSelected = isRowSelected(key, selectedKey);
            const isHovered =
              hoveredKey !== undefined && String(hoveredKey) === String(key);
            const value = row[col.id as keyof T];
            return (
              <div
                key={`${String(key)}-${col.id}`}
                className={`${styles.columnBody} ${isHovered ? styles.hovered : ''} ${
                  isSelected ? styles.selected : ''
                }`}
                onClick={onRowClick ? () => onRowClick(key, row) : undefined}
                onMouseEnter={() => setHoveredKey(key)}
                onMouseLeave={() =>
                  setHoveredKey((prev) =>
                    prev !== undefined && String(prev) === String(key)
                      ? undefined
                      : prev,
                  )
                }
              >
                <div className={styles.cell}>
                  {renderCellValue(value as React.ReactNode)}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </>
  );
}

export default AccountTable;
